import { router } from "@inertiajs/react"
import { Clock, Home, Lightbulb, Plus } from "lucide-react"
import type { PropsWithChildren } from "react"
import { useCallback, useState } from "react"
import { toast } from "sonner"

import { ActionSheet } from "@/components/action-sheet"
import { AppContent } from "@/components/app-content"
import { AppHeader } from "@/components/app-header"
import { AppShell } from "@/components/app-shell"
import { BottomTabBar } from "@/components/bottom-tab-bar"
import { FAB } from "@/components/fab"
import { MealSheet } from "@/components/meal-sheet"
import { StoolSheet } from "@/components/stool-sheet"
import { SymptomSheet } from "@/components/symptom-sheet"
import { dashboardPath, entriesPath, historyPath, insightsPath } from "@/routes"

const mobileNavTabs = [
  { title: "Home", href: dashboardPath(), icon: Home },
  { title: "Insights", href: insightsPath(), icon: Lightbulb },
  { title: "History", href: historyPath(), icon: Clock },
]

const quickLogParams: Record<
  string,
  { kind: string; data: Record<string, string> }
> = {
  Water: { kind: "drink", data: { drink_type: "water" } },
  Coffee: { kind: "drink", data: { drink_type: "coffee" } },
  Tea: { kind: "drink", data: { drink_type: "tea" } },
  Snack: { kind: "meal", data: { meal_type: "snack" } },
}

export default function AppMobileLayout({ children }: PropsWithChildren) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [stoolSheetOpen, setStoolSheetOpen] = useState(false)
  const [symptomSheetOpen, setSymptomSheetOpen] = useState(false)
  const [mealSheetOpen, setMealSheetOpen] = useState(false)

  const handleQuickLog = useCallback((label: string) => {
    const params = quickLogParams[label]
    if (!params) return
    router.post(
      entriesPath(),
      { entry: params },
      {
        preserveScroll: true,
        onSuccess: () => toast(`Logged ${label}`, { duration: 2000 }),
      },
    )
  }, [])

  const handleCheckin = useCallback(
    (item: { type: "mood" | "energy"; level: number }) => {
      router.post(
        entriesPath(),
        { entry: { kind: item.type, data: { level: item.level } } },
        {
          preserveScroll: true,
          onSuccess: () =>
            toast(`Logged ${item.type} — level ${item.level}`, {
              duration: 2000,
            }),
        },
      )
    },
    [],
  )

  const handleLogEntry = useCallback((item: string) => {
    if (item === "Stool") {
      setSheetOpen(false)
      setTimeout(() => setStoolSheetOpen(true), 150)
    } else if (item === "Symptom") {
      setSheetOpen(false)
      setTimeout(() => setSymptomSheetOpen(true), 150)
    } else if (item === "Meal") {
      setSheetOpen(false)
      setTimeout(() => setMealSheetOpen(true), 150)
    }
  }, [])

  const handleStoolLog = useCallback(
    (entry: { type: number; title: string }) => {
      setStoolSheetOpen(false)
      router.post(
        entriesPath(),
        {
          entry: {
            kind: "stool",
            data: { bristol_type: entry.type, description: entry.title },
          },
        },
        {
          preserveScroll: true,
          onSuccess: () =>
            toast(`Logged Type ${entry.type} — ${entry.title}`, {
              duration: 2000,
            }),
        },
      )
    },
    [],
  )

  const handleSymptomLog = useCallback(
    (entry: { symptom_type: string; severity: number; notes?: string }) => {
      setSymptomSheetOpen(false)
      router.post(
        entriesPath(),
        {
          entry: {
            kind: "symptom",
            data: {
              symptom_type: entry.symptom_type,
              severity: entry.severity,
            },
            notes: entry.notes,
          },
        },
        {
          preserveScroll: true,
          onSuccess: () =>
            toast(`Logged ${entry.symptom_type} — ${entry.severity}/5`, {
              duration: 2000,
            }),
        },
      )
    },
    [],
  )

  const handleMealLog = useCallback(
    (entry: {
      ingredients: string[]
      tag_ids: number[]
      notes?: string
      photo_signed_id?: string
    }) => {
      setMealSheetOpen(false)
      router.post(
        entriesPath(),
        {
          entry: {
            kind: "meal",
            data: { ingredients: entry.ingredients },
            notes: entry.notes,
            tag_ids: entry.tag_ids,
            ...(entry.photo_signed_id ? { photo: entry.photo_signed_id } : {}),
          },
        },
        {
          preserveScroll: true,
          onSuccess: () =>
            toast(
              `Logged meal — ${entry.ingredients.length} ingredient${entry.ingredients.length === 1 ? "" : "s"}`,
              { duration: 2000 },
            ),
        },
      )
    },
    [],
  )

  return (
    <AppShell variant="mobile">
      <AppHeader variant="mobile" />
      <AppContent
        variant="mobile"
        sheetOpen={
          sheetOpen || stoolSheetOpen || symptomSheetOpen || mealSheetOpen
        }
      >
        {children}
      </AppContent>
      <ActionSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onQuickLog={handleQuickLog}
        onCheckin={handleCheckin}
        onLogEntry={handleLogEntry}
      />
      <StoolSheet
        open={stoolSheetOpen}
        onClose={() => setStoolSheetOpen(false)}
        onLog={handleStoolLog}
      />
      <SymptomSheet
        open={symptomSheetOpen}
        onClose={() => setSymptomSheetOpen(false)}
        onLog={handleSymptomLog}
      />
      <MealSheet
        open={mealSheetOpen}
        onClose={() => setMealSheetOpen(false)}
        onLog={handleMealLog}
      />
      <div className="absolute right-0 bottom-0 left-0 z-20 flex items-center gap-2.5 px-5 pb-5">
        <BottomTabBar tabs={mobileNavTabs} />
        <FAB
          onClick={() => setSheetOpen((o) => !o)}
          icon={
            <Plus
              size={22}
              strokeWidth={2.5}
              style={{
                transition: "transform 200ms cubic-bezier(0.32, 0.72, 0, 1)",
                transform: sheetOpen ? "rotate(45deg)" : "rotate(0deg)",
              }}
            />
          }
        />
      </div>
    </AppShell>
  )
}
